import React, { useState } from 'react';
import { Phone, Mail, MapPin, Clock, Send, CheckCircle, AlertCircle, Lock, Loader2 } from 'lucide-react';
import { companyInfo } from '../data/mock';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';

// FormSubmit.co - serviciu gratuit pentru trimitere email
// Email-ul va fi trimis la adresa specificată în action URL
const FORMSUBMIT_EMAIL = 'detectiviexperti@gmail.com';

const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    service: '',
    message: ''
  });
  const [gdprConsent, setGdprConsent] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError('');
  };

  const handleServiceChange = (value) => {
    setFormData(prev => ({ ...prev, service: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.phone || !formData.message) {
      setError('Vă rugăm să completați toate câmpurile obligatorii.');
      return;
    }

    if (!gdprConsent) {
      setError('Trebuie să acceptați Politica de Confidențialitate.');
      return;
    }

    setIsLoading(true);
    setError('');

    // Pregătim datele pentru FormSubmit.co
    const formDataToSend = new FormData();
    formDataToSend.append('name', formData.name);
    formDataToSend.append('phone', formData.phone);
    formDataToSend.append('email', formData.email || 'Nu a fost furnizat');
    formDataToSend.append('service', formData.service || 'Nu a fost selectat');
    formDataToSend.append('message', formData.message);
    formDataToSend.append('_subject', `Nou mesaj de contact - ${formData.name} | Detectivi Bacău`);
    formDataToSend.append('_template', 'table'); // Template frumos pentru email
    formDataToSend.append('_captcha', 'false'); // Dezactivăm captcha pentru UX mai bun

    try {
      const response = await fetch(`https://formsubmit.co/ajax/${FORMSUBMIT_EMAIL}`, {
        method: 'POST',
        body: formDataToSend,
        headers: {
          'Accept': 'application/json'
        }
      });

      const result = await response.json();
      
      if (result.success) {
        setSubmitted(true);
        
        // Reset form after 5 seconds
        setTimeout(() => {
          setSubmitted(false);
          setFormData({ name: '', phone: '', email: '', service: '', message: '' });
          setGdprConsent(false);
        }, 5000);
      } else {
        throw new Error('Eroare la trimitere');
      }
      
    } catch (err) {
      console.error('Form submission error:', err);
      setError('A apărut o eroare. Vă rugăm să ne contactați telefonic la ' + companyInfo.phone);
    } finally {
      setIsLoading(false);
    }
  };

  const contactInfo = [
    { icon: Phone, label: 'Telefon', value: companyInfo.phone, href: `tel:${companyInfo.phone}` },
    { icon: Mail, label: 'Email', value: companyInfo.email, href: `mailto:${companyInfo.email}` },
    { icon: MapPin, label: 'Adresă', value: companyInfo.address, href: '#' },
    { icon: Clock, label: 'Program', value: companyInfo.workingHours, href: '#' }
  ];

  return (
    <section id="contact" className="py-24 bg-slate-800/30">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <p className="text-amber-400 text-sm uppercase tracking-wider mb-3 font-semibold">Contact</p>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Contactați-ne
          </h2>
          <p className="text-slate-300 text-lg md:text-xl max-w-2xl mx-auto">
            Consultație gratuită și confidențială. Sunați-ne sau completați formularul.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Contact Info */}
          <div>
            <div className="space-y-6 mb-10">
              {contactInfo.map((item, index) => (
                <a
                  key={index}
                  href={item.href}
                  className="flex items-start gap-5 group"
                >
                  <div className="w-14 h-14 bg-slate-800 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-slate-700 transition-colors">
                    <item.icon className="w-6 h-6 text-amber-400" />
                  </div>
                  <div>
                    <p className="text-slate-400 text-base">{item.label}</p>
                    <p className="text-white text-lg font-medium group-hover:text-amber-400 transition-colors">{item.value}</p>
                  </div>
                </a>
              ))}
            </div>

            <div className="p-6 bg-gradient-to-r from-green-500/10 to-emerald-500/10 border border-green-500/20 rounded-xl">
              <div className="flex items-center gap-3 mb-3">
                <Lock className="w-6 h-6 text-green-400" />
                <span className="text-green-400 text-lg font-semibold">Confidențialitate Garantată</span>
              </div>
              <p className="text-slate-300 text-base">
                Toate informațiile sunt tratate cu maximă discreție conform GDPR. Datele dumneavoastră nu vor fi partajate cu terțe părți.
              </p>
            </div>
            
            {/* Direct call CTA */}
            <div className="mt-8 p-6 bg-amber-500/10 border border-amber-500/30 rounded-xl">
              <p className="text-slate-300 text-base mb-4">
                Preferați să discutați direct? Sunați-ne acum:
              </p>
              <a 
                href={`tel:${companyInfo.phone}`}
                className="inline-flex items-center gap-3 bg-amber-500 hover:bg-amber-600 text-slate-900 font-bold text-lg px-6 py-4 rounded-xl transition-colors"
              >
                <Phone className="w-6 h-6" />
                {companyInfo.phone}
              </a>
            </div>
          </div>

          {/* Form */}
          <div className="bg-slate-800/50 border border-slate-700/50 rounded-2xl p-8 md:p-10">
            {submitted ? (
              <div className="text-center py-16">
                <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                  <CheckCircle className="w-10 h-10 text-green-400" />
                </div>
                <h4 className="text-2xl font-bold text-white mb-3">Mesaj Trimis cu Succes!</h4>
                <p className="text-slate-300 text-lg">Vă vom contacta în cel mai scurt timp posibil.</p>
                <p className="text-slate-400 mt-4">Verificați și folder-ul Spam dacă nu primiți răspuns în 24h.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <h3 className="text-2xl font-bold text-white mb-2">Trimite-ne un Mesaj</h3>
                <p className="text-slate-400 mb-6">Câmpurile marcate cu * sunt obligatorii.</p>
                
                {error && (
                  <div className="flex items-center gap-3 p-4 bg-red-500/10 border border-red-500/20 rounded-xl text-red-400 text-base">
                    <AlertCircle className="w-5 h-5 flex-shrink-0" />
                    {error}
                  </div>
                )}

                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <Label className="text-slate-200 text-base font-medium">Nume *</Label>
                    <Input
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Numele dumneavoastră"
                      className="mt-2 bg-slate-900/50 border-slate-700 text-white placeholder:text-slate-500 h-12 text-base"
                      disabled={isLoading}
                      required
                    />
                  </div>
                  <div>
                    <Label className="text-slate-200 text-base font-medium">Telefon *</Label>
                    <Input
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="+40 7XX XXX XXX"
                      className="mt-2 bg-slate-900/50 border-slate-700 text-white placeholder:text-slate-500 h-12 text-base"
                      disabled={isLoading}
                      required
                    />
                  </div>
                </div>

                <div>
                  <Label className="text-slate-200 text-base font-medium">Email</Label>
                  <Input
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="email@exemplu.ro"
                    className="mt-2 bg-slate-900/50 border-slate-700 text-white placeholder:text-slate-500 h-12 text-base"
                    disabled={isLoading}
                  />
                </div>

                <div>
                  <Label className="text-slate-200 text-base font-medium">Serviciu Dorit</Label>
                  <Select value={formData.service} onValueChange={handleServiceChange} disabled={isLoading}>
                    <SelectTrigger className="mt-2 bg-slate-900/50 border-slate-700 text-white h-12 text-base">
                      <SelectValue placeholder="Selectați serviciul" />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700">
                      <SelectItem value="Verificare Fidelitate Partener" className="text-white text-base">Verificare Fidelitate</SelectItem>
                      <SelectItem value="Background Check Corporate" className="text-white text-base">Background Check</SelectItem>
                      <SelectItem value="Detectare Camere/Microfoane Spion" className="text-white text-base">Detectare Camere/Microfoane</SelectItem>
                      <SelectItem value="Test Poligraf (Detector Minciuni)" className="text-white text-base">Test Poligraf</SelectItem>
                      <SelectItem value="Investigare Fraude Asigurări" className="text-white text-base">Fraude Asigurări</SelectItem>
                      <SelectItem value="Alt Serviciu" className="text-white text-base">Altele</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="text-slate-200 text-base font-medium">Mesaj *</Label>
                  <Textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Descrieți pe scurt situația dumneavoastră. Toate informațiile sunt confidențiale."
                    rows={5}
                    className="mt-2 bg-slate-900/50 border-slate-700 text-white placeholder:text-slate-500 resize-none text-base"
                    disabled={isLoading}
                    required
                  />
                </div>

                <div className="flex items-start gap-3 p-4 bg-slate-900/30 rounded-xl">
                  <Checkbox
                    id="gdpr"
                    checked={gdprConsent}
                    onCheckedChange={setGdprConsent}
                    className="mt-0.5 border-slate-600 data-[state=checked]:bg-amber-500 data-[state=checked]:border-amber-500"
                    disabled={isLoading}
                  />
                  <label htmlFor="gdpr" className="text-slate-300 text-base cursor-pointer">
                    Accept <span className="text-amber-400 hover:underline">Politica de Confidențialitate</span> și sunt de acord cu prelucrarea datelor conform GDPR. *
                  </label>
                </div>

                <Button
                  type="submit"
                  disabled={isLoading}
                  className="w-full bg-amber-500 hover:bg-amber-600 text-slate-900 font-bold text-lg py-7 rounded-xl transition-colors"
                >
                  {isLoading ? (
                    <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Se trimite mesajul...</>
                  ) : (
                    <><Send className="w-5 h-5 mr-2" /> Trimite Mesajul</>
                  )}
                </Button>
                
                <p className="text-slate-500 text-sm text-center">
                  Veți primi un răspuns în maximum 24 de ore.
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
